/**
 *  [2022] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

/**
 * :ZNOTE:
 * tag: journalManage
 * note:
 *  - journalManageの本体
 *  - あらゆるものをログとして追記保存する（削除や変更はできない）
 *  - topicごとにログを管理できる（topicがHashKeyになっている）
 *  - IndexedDBにキャッシュする/しないは、topicごとに設定できる
 *  - CacheManageのコードをかなり流用している。一部サブクラスはimportして使っている
 *  - 仕様は、docs/common/ログ管理機能.pptx に記載
 * sample:
 *  - ほぼ全てのプロジェクト?
 * :ZNOTE:
 */


import {Dexie} from 'dexie';
import {Base64} from 'js-base64';
import pako  from 'pako';

import {
  IJournalRecord,
  createRecordProc, bulkCreateRecordProc,
  readRecordsProc, getMetaInfoProc,
  archiveForTesting, flushForTesting,
} from '@/appLogic/stacks/journalManagement';

import {BaseDbStructure} from './interfaces';
import {getEmail} from '@zettant/awsUtils';
import {getRandomStr} from '@zettant/randomUtils';
import {CacheManageTimestamp} from '@zettant/cacheManage/cacheManageTimestamp';
import {InMemoryCache} from '@zettant/cacheManage/inMemoryCache';

const DB_VERSION = 1;

type TStoreMode = 'defaultNoStore' | 'defaultStore';

const dbInit = (tableName: string) => {
  const db: any = new Dexie(tableName);
  db.version(DB_VERSION).stores({
    entries: '[topic+dateStr]',
    meta: 'name',
    offline: '[topic+dateStr]',
  });
  return {
    db,
    entries: db.entries,
    meta: db.meta,
    offline: db.offline,
  };
};


class JournalManage {
  protected db: BaseDbStructure;
  protected readonly tableName: string;
  protected readonly user: string;
  protected dataCache: InMemoryCache;
  protected timestampManage: CacheManageTimestamp;
  protected lastReceivedForAvoidDup: {[key:string]: (string|number)[]} = {};  // getByTime()は直前に受信したメッセージの最終時刻と同時刻のものを次も受信する仕様なので、アプリ側にそれを伝えないようにするためにメモを置いておく
  private tmpBuffer: IJournalRecord[] = [];
  private storeMode: TStoreMode = 'defaultNoStore';  // または'defaultStore'
  private topicsToStore: string[] = [];

  /**
   * コンストラクタ
   * @param tableName {string}
   */
  constructor(tableName: string) {
    this.tableName = tableName;
    this.db = dbInit(this.tableName);
    this.dataCache = new InMemoryCache('dataId', this.getFromIndexedDb);
    this.timestampManage = new CacheManageTimestamp(this.db.meta, 'topic');
    this.user = getEmail();
  }

  public getDb = () => this.db;

  /**
   * storeModeを'defaultStore'にする
   * defaultStoreの場合、指定がなければ（this.topicsToStoreに登録がなければ）、そのtopicはIndexedDBにログデータを保存するようになる
   */
  public changeStoreModeToDefaultStore = () => this.storeMode = 'defaultStore';
  public addTopicsToStore = (topics: string[]) => this.topicsToStore = [...this.topicsToStore, ...topics];
  public delTopicToStore = (topic: string) => {
    this.topicsToStore = this.topicsToStore.filter((t) => t !== topic);
  };

  /**
   * そのtopicがIndexedDBに保存されるべきものかどうかを調べる
   * @param topic
   */
  private _shouldStoreInIndexedDB = (topic: string) => {
    const idx = this.topicsToStore.findIndex((t) => t === topic);
    if (this.storeMode === 'defaultNoStore' && idx === -1) return false;
    if (this.storeMode === 'defaultStore' && idx !== -1) return false;
    return true;
  };

  /**
   * primaryKeyを指定して該当する全てのレコードをIndexedDbから取得するユーティリティ
   *   arrow関数で定義しないと、InMemoryCacheオブジェクトから正しくアクセスできない（this.dbが未定義になる）
   * @param topic {string}
   * @protected
   */
  public getFromIndexedDb = (topic: string): Promise<any> => {
    if (!this._shouldStoreInIndexedDB(topic)) {
      return <any>[];
    }
    return this.db.entries.where(['topic', 'dateStr']).between([topic, Dexie.minKey], [topic, Dexie.maxKey], true, true).toArray();
  };

  public setLatestTimestampInfo = (allInfo: {[key:string]: number}) => this.timestampManage.setLatest(allInfo);

  /**
   * 最新時刻を巻き戻す（不具合が起こったときのため）
   * @param primaryKeyValue {string}
   * @param timeToRewind {number}
   */
  public rewindTimestamp = async (primaryKeyValue: string, timeToRewind: number) => this.timestampManage.rewindTimestamp(primaryKeyValue, timeToRewind);

  /**
   * 最新時刻を強制設定する
   * @param primaryKeyValue {string}
   * @param timestamp_millisec {number}
   */
  public setLatestTimestamp = async (primaryKeyValue: string, timestamp_millisec: number) => this.timestampManage.setLatestTimestamp(primaryKeyValue, timestamp_millisec);

  public getLatestTimestamp = (primaryKeyValue: string) => this.timestampManage.getLatest(primaryKeyValue);

  /**
   * IndexedDBをすべてスキャンして、this.latestを再構築する
   *  (テスト用にpublicにしているが、本来はprivateでいい)
   * @private
   */
  public checkAllLatestTimestamps = async (allEntries: any) => this.timestampManage.checkAllLatestTimestamps(allEntries);


  /**
   * メタ情報の取得
   */
  public async getMetaInfo() {
    const retDatB64 = await getMetaInfoProc();
    if (!retDatB64) return;
    return JSON.parse(pako.inflate(Base64.toUint8Array(retDatB64), {to: 'string'}));
  }

  /**
   * ログを取得する
   * @param topic
   * @param startAt
   * @param endAt
   * @param user
   */
  public async readLogs(topic: string, startAt?: number, endAt?: number, user?: string): Promise<IJournalRecord[]> {
    await this.dataCache.loadInMemory(topic);  // まずはメモリに確実に読み出しておく

    // まずは、サーバから情報を取得する (cacheManageClassのgetNewRecordsFromServerとほぼ同じ処理)
    await this.getNewRecordsFromServer(topic, startAt, endAt, user);

    // データをアプリ層に返答する
    return this.dataCache.getAllForKey(topic)
      .filter((ent) => {
        if (startAt && ent.timeOfRecord < startAt) return false;
        if (endAt && ent.timeOfRecord > endAt) return false;
        if (!user) return true;
        return ent.user !== user;
      })
      .sort((a: any, b: any) => a.timeOfRecord - b.timeOfRecord);
  }

  /**
   * サーバから情報を取得する (cacheManageClassのgetNewRecordsFromServerとほぼ同じ処理)
   * @param topic
   * @param startAt
   * @param endAt
   * @param user
   * @private
   */
  private async getNewRecordsFromServer(topic: string, startAt?: number, endAt?: number, user?: string) {
    const latest = this.getLatestTimestamp(topic);  // そのtopicがIndexedDBに登録すべきものなら、リロードしてもtimestampは0にならない
    const retDatB64 = await readRecordsProc(topic, startAt, endAt, (this._shouldStoreInIndexedDB(topic)) ? latest : undefined, user);
    if (!retDatB64) return;
    const resultsFromServer: any = JSON.parse(pako.inflate(Base64.toUint8Array(retDatB64), {to: 'string'}));
    // resultsFromServer = {<dateStr>: [<record>,,,]}
    // console.log('>>>>>>>', latest, resultsFromServer);

    const latestInResult = Math.max(Object.values(resultsFromServer).map((r: any) => r.updatedAt) as any);
    if (!resultsFromServer || Object.keys(resultsFromServer).length === 0 || (latest !== 0 && latest === latestInResult)) {
      // 更新がなかった場合
      this.timestampManage.update(topic, latest+1);  // 何も更新がなかったということなので、とりあえず1ミリ秒進めておいて今後の重複受信を防ぐ
      await this.timestampManage.registerLatest();
      this.lastReceivedForAvoidDup[topic] = [];
    }

    // EXP: 前回取得した最新のメッセージと同一時刻のメッセージを受信してしまうため、それを削除するためのメモを適用する
    if (this.lastReceivedForAvoidDup[topic] && this.lastReceivedForAvoidDup[topic].length > 0) {
      Object.keys(resultsFromServer).forEach((k) => {
        resultsFromServer[k] = resultsFromServer[k].filter((ent) =>
          !this.lastReceivedForAvoidDup[topic].includes(ent.dataId) || this.lastReceivedForAvoidDup[topic][0] < ent.updatedAt
        );
      });
    }

    // EXP: 次回以降のアクセスに備えて、latestTimestampと２重受信を防ぐためのメモ(lastReceivedForAvoidDup)を更新する
    let latestTimestamp = 0;
    Object.values(resultsFromServer).forEach((archiveEntries: any) =>
      archiveEntries.forEach((ent) => {
        if (ent.updatedAt > latestTimestamp) {
          latestTimestamp = ent.updatedAt;
          this.lastReceivedForAvoidDup[topic] = [latestTimestamp, ent.dataId];  // 1番目の要素に時刻を入れておく
        } else if (ent.updatedAt === latestTimestamp) {
          this.lastReceivedForAvoidDup[topic].push(ent.dataId);
        }
      }));

    // EXP: そのtopicの最新レコード時刻の情報を更新する
    if (latestTimestamp > 0) {
      this.timestampManage.update(topic, latestTimestamp);
    }

    // EXP: サーバから取得した情報をメモリと、場合によってはIndexedDBに書き込む
    if (Object.keys(resultsFromServer).length > 0) {
      await this.putDataInCache(topic, resultsFromServer);  // resultsFromServer = {<dateStr>: [<record>,,,]}
    }
  }

  private async putDataInCache(topic: string, datToRegister: any) {
    const promises: any = [];
    if (this._shouldStoreInIndexedDB(topic)) {
      promises.push(this.timestampManage.registerLatest());
    }
    for (const dateStr in datToRegister) {
      promises.push(this.dataCache.updateInMemory(topic, {topic, dateStr, '__archivedEntries': datToRegister[dateStr]}));
      if (this._shouldStoreInIndexedDB(topic)) {
        // そのtopicがIndexedDBに登録すべきものならIndexedDBにも書き込む
        const x = await this.db.entries.get([topic, dateStr]); // 元々登録されていたものがあれば、上書き登録する
        if (x && x['__archivedEntries']) {
          // EXP: 重複を削除する
          // TIPS: ただし、sortでupdatedAt昇順に並べてから処理することで、より新しい(updatedAtが大きい)ものが生き残るようにする(map化する時に新しい方で上書きされるため)
          const newEntries = Array.from(
            new Map([...x['__archivedEntries'], ...datToRegister[dateStr]].sort((a, b) => a.updatedAt - b.updatedAt).map((ent) => [ent.dataId, ent])).values()
          );
          promises.push(this.db.entries.put({topic, dateStr, '__archivedEntries': newEntries}));
        }
        else {
          promises.push(this.db.entries.put({topic, dateStr, '__archivedEntries': datToRegister[dateStr]}));
        }
      }
    }
    await Promise.all(promises);
  }

  /**
   * ログデータを作成する
   * @param topic
   * @param message
   * @param info
   */
  protected createLog = (topic: string, message?: string, info?: any): IJournalRecord => {
    const timeOfRecord = new Date().getTime();
    const logData: IJournalRecord = {
      topic,
      dataId: getRandomStr(10),
      timeOfRecord,
      user: this.user,
      message,
    };
    if (info) {
      logData['json'] = Base64.fromUint8Array(pako.deflate(JSON.stringify(info)));
    }
    return logData;
  };

  /**
   * ログを登録する
   *
   * @param topic {string}
   * @param message {string}
   * @param info {any}  任意のオブジェクト
   */
  public async put(topic: any, message?: string, info?: any) {
    const logData = this.createLog(topic, message, info);
    return await createRecordProc(logData);
  }

  /**
   * ログを登録する(テスト用)
   *
   * @param topic {string}
   * @param message {string}
   * @param info {any}  任意のオブジェクト
   * @param timeOfRecord {number}  テストの時にしか使わない
   */
  public async testPut(topic: any, message?: string, info?: any, timeOfRecord?: number) {
    const logData = this.createLog(topic, message, info);
    if (timeOfRecord) logData.timeOfRecord = timeOfRecord;
    return await createRecordProc(logData);
  }

  /**
   * ログを一時的にバッファして、後でbulkPutする
   * @param topic
   * @param message
   * @param info
   */
  public addLogBuffer(topic: any, message?: string, info?: any) {
    const logData = this.createLog(topic, message, info);
    this.tmpBuffer.push(logData);
  }

  /**
   * tmpBufferに溜まったログをbulkPutする
   */
  public async bulkPut(): Promise<boolean> {
    if (this.tmpBuffer.length === 0) return false;
    const recordsString = Base64.fromUint8Array(pako.deflate(JSON.stringify(this.tmpBuffer)));
    const res = await bulkCreateRecordProc(recordsString);
    if (res) this.tmpBuffer = [];
    return res;
  }

  /**
   * ローカルストレージのこのテーブルの項目を削除する。これを実行したらオブジェクトが実質的に無効になるので注意。
   */
  public async flush() {
    this.db = dbInit(this.tableName);
    await this.db.meta.clear(); // delete仕切れていない時がある様なので念の為。metaさえ消せばtimestampがリセットされるので、サーバとの再動機ができる
    await this.db.meta.clear();
    await this.db.entries.clear();
    await this.db.offline.clear();
    this.dataCache = new InMemoryCache( 'dataId', this.getFromIndexedDb);
    this.timestampManage = new CacheManageTimestamp(this.db.meta, 'topic');
  }

  /**
   * テスト用の全件取得関数
   */
  public rawGet = async () => this.db.entries.toArray();

  /**
   * テスト用の関数（dev環境でだけLambdaが受け付ける）
   */
  public async flushForTesting () {
    await flushForTesting();
  }

  /**
   * テスト用の関数（dev環境でだけLambdaが受け付ける）
   */
  public async archiveForTesting () {
    await archiveForTesting();
  }

}

export {JournalManage};
